import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface NotifierState {
  message: string;
  severity: "error" | "success" | "info" | "warning";
  show: boolean;
  title?: string;
}
interface DashboardData {
  currency?: string;
  amount: number;
  corporateName?: string;
  logoUrl?: string;
  showCorporateSwitch?: boolean;  
}

interface ProfileData {
  corporate_name: string;
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
  enabled_navigation?: EnabledNavigation;
}

export interface EnabledNavigation {
  credit_upload: boolean;
  credit_reports: boolean;
  saved_orders: boolean;
  user_management: boolean;
  expiry_reminders: boolean;
  reports: boolean;
  wallet_balance: boolean;
}

interface CommonState {
  notifier: NotifierState;
  uploadListSnapshot: any | null;
  dashboardData?: DashboardData;
  profileData?: ProfileData;
}

const initialState: CommonState = {
  notifier: {
    message: "",
    severity: "success",
    show: false,
    title: "",
  },
  uploadListSnapshot: null,
  dashboardData: {
    currency: "",
    amount: 0,
    corporateName: "",
    logoUrl: "",
    showCorporateSwitch: false,
  },
  profileData: {
    corporate_name: "",
    email: "",
    full_name: "",
    first_name: "",
    last_name: "",
    enabled_navigation: {
      credit_upload: false,
      credit_reports: false,
      saved_orders: false,
      user_management: false,
      expiry_reminders: false,
      reports: false,
      wallet_balance: false,
    },
  },
};

const commonSlice = createSlice({
  name: "common",
  initialState,
  reducers: {
    notifyUser: (
      state,
      action: PayloadAction<{
        message: string;
        severity?: "error" | "success" | "info" | "warning";
        title?: string;
      }>,
    ) => {
      state.notifier.message = action.payload.message;
      state.notifier.title = action.payload.title;
      state.notifier.severity = action.payload.severity || "success";
      state.notifier.show = true;
    },
    closeNotifier: (state) => {
      state.notifier.message = "";
      state.notifier.severity = "success";
      state.notifier.show = false;
    },
    setUploadListSnapshot: (state, action: PayloadAction<any>) => {
      state.uploadListSnapshot = action.payload;
    },
    clearUploadListSnapshot: (state) => {
      state.uploadListSnapshot = null;
    },
    setDashboardData: (state, action: PayloadAction<DashboardData>) => {
      state.dashboardData = action.payload;
    },
    setProfileData: (state, action: PayloadAction<ProfileData>) => {
      state.profileData = action.payload;
    },
  },
});

export const {
  notifyUser,
  closeNotifier,
  setUploadListSnapshot,
  clearUploadListSnapshot,
  setDashboardData,
  setProfileData,
} = commonSlice.actions;

export default commonSlice.reducer;

// Selectors
import { RootState } from "../store";

export const selectNotifier = (state: RootState) => state.common.notifier;
export const selectUploadListSnapshot = (state: RootState) =>
  state.common.uploadListSnapshot;
export const selectDashboardData = (state: RootState) =>
  state.common.dashboardData;

export const selectProfileData = (state: RootState) => state.common.profileData;
